import React from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/AlertDialog'
import {
  FeatureGate,
  useFeatureHidden,
  useFeaturesLoaded,
  useHasFeature,
} from '@/components/feature-gate'
import {
  XeroConnectButton,
  XeroDisconnectButton,
  XeroLogo,
} from '@/components/integrations/XeroButton'
import {
  ACCOUNTING_PROVIDERS,
  type AccountingProvider,
} from '@/services/api/integrations/accountingProviders'
import {
  useAccountingConnection,
  useDisconnectAccounting,
  useGatedAccountingConnection,
  useResumeAccountingSync,
  useStartAccountingConnect,
} from '@/services/api/integrations/useAccountingConnection'
import { toast } from 'sonner'

export default function IntegrationsSection() {
  return (
    <div className="space-y-6">
      <div>
        <h2>Integrations</h2>
        <p className="text-muted-foreground">
          Connect this Space to your accounting software
        </p>
      </div>

      <Separator />

      <AccountingConnectionCard provider="xero" />
      <AccountingConnectionCard provider="zoho" />
    </div>
  )
}

function AccountingConnectionCard({
  provider,
}: {
  provider: AccountingProvider
}) {
  const { label, feature, organisation } = ACCOUNTING_PROVIDERS[provider]
  // useHasFeature is optimistic while the plan catalogue loads — wait for it,
  // so a Space without the feature never calls the gated status endpoint.
  const hasFeature = useHasFeature(feature)
  const featuresLoaded = useFeaturesLoaded()
  // Locked and not to be shown (Basic): no card, not even a locked one.
  const hidden = useFeatureHidden(feature)

  if (hidden) return null

  return (
    <Card className="w-full p-6">
      <div className="mb-4 flex items-center gap-3">
        {provider === 'xero' ? (
          <XeroLogo className="h-7" />
        ) : (
          <span className="shrink-0 whitespace-nowrap text-lg font-semibold">
            {label}
          </span>
        )}
        <div>
          {provider === 'xero' && <h3 className="sr-only">{label}</h3>}
          <p className="text-sm text-muted-foreground">
            Import bank transactions from one {organisation} into this Space.
          </p>
        </div>
      </div>

      <FeatureGate
        feature={feature}
        mode="block"
        blockLabel={
          <>
            <strong>{label} integration</strong> is not included in this Space's
            plan.
          </>
        }
      >
        {featuresLoaded && hasFeature ? (
          <AccountingConnectionBody provider={provider} />
        ) : (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
      </FeatureGate>
    </Card>
  )
}

/** Xero's own button artwork for Xero (a certification rule); the app's buttons otherwise. */
function ConnectButton({
  provider,
  onClick,
  loading,
}: {
  provider: AccountingProvider
  onClick: () => void
  loading: boolean
}) {
  if (provider === 'xero') {
    return <XeroConnectButton onClick={onClick} loading={loading} />
  }

  return (
    <Button onClick={onClick} disabled={loading} loading={loading}>
      Connect {ACCOUNTING_PROVIDERS[provider].label}
    </Button>
  )
}

function AccountingConnectionBody({
  provider,
}: {
  provider: AccountingProvider
}) {
  const { label, slug, organisation } = ACCOUNTING_PROVIDERS[provider]
  const otherProvider: AccountingProvider =
    provider === 'xero' ? 'zoho' : 'xero'
  const { data, isPending } = useAccountingConnection(provider)
  // A Space imports from one accounting system at a time.
  const { data: other } = useGatedAccountingConnection(otherProvider)
  const { action: startConnect, status: startStatus } =
    useStartAccountingConnect(provider)
  const { action: disconnect, status: disconnectStatus } =
    useDisconnectAccounting(provider)
  const { action: resume, status: resumeStatus } =
    useResumeAccountingSync(provider)

  if (isPending || !data) {
    return <p className="text-sm text-muted-foreground">Loading…</p>
  }

  const { connection, can_manage: canManage } = data
  // The API explains a refusal (e.g. another provider connected); httpClient shows it.
  const handleConnect = () => startConnect().catch(() => undefined)

  if (!connection) {
    if (other?.connection) {
      return (
        <p className="text-sm text-muted-foreground">
          This Space is connected to {ACCOUNTING_PROVIDERS[otherProvider].label}
          . Disconnect it to connect {label} instead.
        </p>
      )
    }

    return canManage ? (
      <ConnectButton
        provider={provider}
        onClick={handleConnect}
        loading={startStatus.isPending}
      />
    ) : (
      <p className="text-sm text-muted-foreground">
        {label} is not connected. Ask your Space admin to connect {label}.
      </p>
    )
  }

  const stopped = connection.needs_reconnect || connection.status === 'paused'

  const handleDisconnect = async () => {
    await disconnect()
    toast.success(`${label} disconnected. Imported transactions were kept.`)
  }

  const handleResume = async () => {
    try {
      await resume()
      toast.success(`${label} sync resumed.`)
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ?? `Could not resume ${label} sync.`,
      )
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {connection.needs_attention ? (
          <AlertTriangle className="size-4 text-amber-600" />
        ) : (
          <CheckCircle2 className="size-4 text-green-600" />
        )}
        <span className="font-medium">
          Connected to {connection.tenant_name ?? organisation}
        </span>
        <Badge variant={stopped ? 'destructive' : 'default'}>
          {statusLabel(connection.status)}
        </Badge>
      </div>

      <div className="grid grid-cols-1 gap-2 text-sm text-muted-foreground md:grid-cols-2">
        {connection.connected_by && (
          <p>
            Connected by {connection.connected_by.name}{' '}
            {connection.connected_by.last_name ?? ''}
          </p>
        )}
        <p>
          Last sync:{' '}
          {connection.last_sync_at
            ? new Date(connection.last_sync_at).toLocaleString()
            : 'not yet'}
        </p>
      </div>

      {stopped && (
        <div className="space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p>
            {connection.needs_reconnect
              ? `${label} authorisation has expired or was revoked, so syncing has stopped.`
              : 'Syncing is paused after repeated errors.'}{' '}
            {canManage
              ? connection.needs_reconnect
                ? `Reconnect ${label} to resume.`
                : `Resume syncing, or reconnect ${label} if the problem persists.`
              : 'Ask your Space admin to fix the connection.'}
          </p>
          {connection.pause_reason && (
            <p className="text-xs">Last error: {connection.pause_reason}</p>
          )}
        </div>
      )}

      {!stopped && connection.rate_limited_until && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {label}'s daily request limit for this organisation has been reached.
          Syncing resumes automatically after{' '}
          {new Date(connection.rate_limited_until).toLocaleString()}.
        </div>
      )}

      {(connection.unmapped_bank_accounts > 0 ||
        connection.invoices_pending_mapping > 0) && (
        <div className="rounded-md border p-3 text-sm">
          <strong>Needs mapping:</strong>
          <ul className="mt-1 list-disc pl-5">
            {connection.unmapped_bank_accounts > 0 && (
              <li>
                {plural(connection.unmapped_bank_accounts, 'bank account')}{' '}
                without a vault. Their transactions can't be placed in a vault
                until mapped.
              </li>
            )}
            {connection.invoices_pending_mapping > 0 && (
              <li>
                {plural(
                  connection.invoices_pending_mapping,
                  'invoice or bill',
                  'invoices or bills',
                )}{' '}
                waiting for a category or vault before they can be forecast.
              </li>
            )}
          </ul>
        </div>
      )}

      <Link to={`/integrations/${slug}/mapping`}>
        <Button variant="outline">Map accounts to vaults and categories</Button>
      </Link>

      {canManage && (
        <div className="flex flex-wrap gap-2">
          {connection.status === 'paused' && (
            <Button
              variant="outline"
              onClick={handleResume}
              disabled={resumeStatus.isPending}
            >
              <RefreshCw className="mr-1 size-4" />
              Resume sync
            </Button>
          )}

          {stopped && (
            <ConnectButton
              provider={provider}
              onClick={handleConnect}
              loading={startStatus.isPending}
            />
          )}

          <AlertDialog>
            <AlertDialogTrigger asChild>
              {provider === 'xero' ? (
                <XeroDisconnectButton loading={disconnectStatus.isPending} />
              ) : (
                <Button
                  variant="destructiveOutline"
                  disabled={disconnectStatus.isPending}
                >
                  Disconnect {label}
                </Button>
              )}
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Disconnect {label}</AlertDialogTitle>
                <AlertDialogDescription>
                  Syncing from "{connection.tenant_name}" will stop for everyone
                  in this Space. Transactions already imported are kept. You can
                  reconnect at any time.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDisconnect}>
                  Disconnect
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  )
}

function plural(count: number, singular: string, pluralForm = `${singular}s`) {
  return `${count} ${count === 1 ? singular : pluralForm}`
}

function statusLabel(status: string) {
  switch (status) {
    case 'active':
      return 'Active'
    case 'paused':
      return 'Paused'
    case 'error':
      return 'Needs reconnect'
    default:
      return status
  }
}
